import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

interface PITIDonutChartProps {
  principalInterest: number;
  taxes: number;
  insurance: number;
  hoa: number;
  pmi: number;
  totalPayment: number;
}

const COLORS = {
  principalInterest: 'hsl(199, 89%, 48%)',
  taxes: 'hsl(262, 83%, 58%)',
  insurance: 'hsl(38, 92%, 50%)',
  hoa: 'hsl(160, 84%, 39%)',
  pmi: 'hsl(0, 84%, 60%)',
};

const PITIDonutChart: React.FC<PITIDonutChartProps> = ({
  principalInterest,
  taxes,
  insurance,
  hoa,
  pmi,
  totalPayment,
}) => {
  const fmt = (value: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  const data = [
    { name: 'Principal & Interest', value: principalInterest, color: COLORS.principalInterest },
    { name: 'Taxes', value: taxes, color: COLORS.taxes },
    { name: 'Insurance', value: insurance, color: COLORS.insurance },
    ...(hoa > 0 ? [{ name: 'HOA', value: hoa, color: COLORS.hoa }] : []),
    ...(pmi > 0 ? [{ name: 'PMI', value: pmi, color: COLORS.pmi }] : []),
  ].filter(item => item.value > 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      const pct = ((item.value / totalPayment) * 100).toFixed(1);
      return (
        <div className="bg-card border border-border rounded-lg p-2 shadow-lg text-xs">
          <p className="font-medium text-foreground">{item.name}</p>
          <p className="text-muted-foreground">{fmt(item.value)} ({pct}%)</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex items-center gap-4 pt-2">
      <div className="w-28 h-28 flex-shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={30}
              outerRadius={48}
              paddingAngle={2}
              dataKey="value"
              strokeWidth={0}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-col gap-1.5 min-w-0 flex-1">
        {data.map((item, index) => (
          <div key={index} className="flex items-center gap-2 text-xs">
            <div
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-muted-foreground truncate">{item.name}</span>
            <span className="font-mono font-medium text-foreground ml-auto">
              {fmt(item.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PITIDonutChart;
